import {
    keepPreviousData,
    useQuery,
    type UseQueryOptions,
    type UseQueryResult,
} from "@tanstack/react-query";

import { useCustomFetchClient } from "@/api/mutator/custom-fetch";
import { ODataParams, toODataParams } from "@/lib/odata/to-odata-params";

/** The DevExtreme `x-odata-extreme` response envelope. */
type ODataEnvelope<TRow> = { d: { __count: string; results: TRow[] } };

/** Args accepted by the generated per-endpoint server hooks. */
export type ODataServerArgs = ODataParams;

/**
 * TanStack query options callers may forward to {@link useODataQuery} (and the
 * generated per-endpoint shims). `queryKey`/`queryFn` are owned by the core and
 * therefore excluded — everything else `useQuery` accepts is passthrough.
 */
export type ODataQueryOptions<TRow> = Omit<
    UseQueryOptions<ODataEnvelope<TRow> | TRow[], Error>,
    "queryKey" | "queryFn"
>;

export type UseODataQueryResult<TRow> = Omit<
    UseQueryResult<ODataEnvelope<TRow> | TRow[], Error>,
    "data"
> & {
    data: TRow[];
    rowCount: number;
};

const X_ODATA_EXTREME = { "x-odata-extreme": "true" } as const;

/**
 * Normalise a raw OData response into `{ rows, rowCount }`, derived purely from
 * its shape: a `{ d: { __count, results } }` envelope unwraps to its
 * `results`/`__count`, while a bare array reports `rowCount = rows.length`.
 */
const unwrapResponse = <TRow>(
    data: ODataEnvelope<TRow> | TRow[] | undefined,
): { rows: TRow[]; rowCount: number } => {
    if (
        typeof data === "object" &&
        data !== null &&
        !Array.isArray(data) &&
        "d" in data &&
        Array.isArray(data.d?.results)
    ) {
        return { rows: data.d.results, rowCount: Number(data.d.__count ?? 0) };
    }

    const rows = Array.isArray(data) ? (data as TRow[]) : [];
    return { rows, rowCount: rows.length };
};

/**
 * Single runtime core for OData reads. Serialises the typed filter/sort/page
 * model via {@link toODataParams} and normalises the response to
 * `{ data, rowCount }`.
 *
 * `rows`/`rowCount` are derived purely from what the server returns: a
 * `{ d: { __count, results } }` envelope unwraps to its `results`/`__count`,
 * while a bare array reports `rowCount = rows.length`. The DevExtreme
 * `x-odata-extreme` header — which asks the server for that paginated envelope
 * — is sent only when the caller actually paginates (`top`/`skip` present).
 *
 * Auth + `x-external-org-id` come for free via `useCustomFetchClient`.
 */
export function useODataQuery<TRow>(
    endpoint: string,
    args: ODataParams = {},
    queryOptions?: ODataQueryOptions<TRow>,
): UseODataQueryResult<TRow> {
    const params = toODataParams(args);
    const withPagination = args.top != null || args.skip != null;

    const fetchOData = useCustomFetchClient<ODataEnvelope<TRow> | TRow[]>();

    const query = useQuery({
        queryKey: [endpoint, { params }] as const,
        queryFn: ({ signal }) =>
            fetchOData({
                url: endpoint,
                method: "GET",
                params,
                headers: withPagination ? { ...X_ODATA_EXTREME } : undefined,
                signal,
            }),
        placeholderData: keepPreviousData,
        ...queryOptions,
    });

    const { rows, rowCount } = unwrapResponse(query.data);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { data: _, ...queryRest } = query;
    return {
        ...queryRest,
        data: rows,
        rowCount,
    };
}
